import { plegar } from './receta';

/** Una opción de una lista larga: lo que se guarda y lo que se lee. */
export interface Opcion {
  valor: string;
  /** El nombre, como se lee en la lista y en el campo cuando ya se eligió. */
  etiqueta: string;
  /** Una segunda línea, más apagada: el código, cuánto queda, el proveedor. */
  detalle?: string | undefined;
  /** Texto que también encuentra la opción sin enseñarse: código, categoría. */
  tambien?: string | undefined;
}

/**
 * Las opciones que contienen cada palabra de lo buscado.
 *
 * Palabra por palabra y en cualquier orden: «kola inca» encuentra «Inca Kola
 * 500 ml», y «beb 500» la encuentra por su código y su tamaño. Sin tildes ni
 * mayúsculas, como el buscador de la receta, porque en la cocina nadie escribe
 * «ají» con tilde en un teclado de tablet.
 *
 * Primero las que **empiezan** por lo buscado: quien teclea «pol» busca
 * «Pollo entero», no «Chaufa de pollo».
 */
export function filtrarOpciones(
  opciones: readonly Opcion[],
  texto: string,
): Opcion[] {
  const palabras = plegar(texto).split(/\s+/).filter((palabra) => palabra !== '');
  if (palabras.length === 0) return [...opciones];

  const encontradas = opciones.filter((opcion) => {
    const dondeBuscar = plegar(
      `${opcion.etiqueta} ${opcion.detalle ?? ''} ${opcion.tambien ?? ''}`,
    );
    return palabras.every((palabra) => dondeBuscar.includes(palabra));
  });

  const primera = palabras[0]!;
  return [
    ...encontradas.filter((opcion) => plegar(opcion.etiqueta).startsWith(primera)),
    ...encontradas.filter((opcion) => !plegar(opcion.etiqueta).startsWith(primera)),
  ];
}

/**
 * Las filas de una tabla que coinciden con lo buscado, por los textos que se
 * le digan de cada una. Mismo criterio que [filtrarOpciones].
 */
export function filtrarFilas<T>(
  filas: readonly T[],
  texto: string,
  textosDe: (fila: T) => (string | null | undefined)[],
): T[] {
  const palabras = plegar(texto).split(/\s+/).filter((palabra) => palabra !== '');
  if (palabras.length === 0) return [...filas];

  return filas.filter((fila) => {
    const dondeBuscar = plegar(textosDe(fila).filter(Boolean).join(' '));
    return palabras.every((palabra) => dondeBuscar.includes(palabra));
  });
}

/**
 * Un insumo como opción de un buscador: el nombre arriba y, debajo, su código
 * con lo que este formulario necesita saber —cuánto queda, de quién es—. Se
 * encuentra también por su categoría.
 */
export function opcionDeInsumo(
  insumo: { id: string; sku: string; name: string; category?: string },
  detalle: string,
): Opcion {
  return {
    valor: insumo.id,
    etiqueta: insumo.name,
    detalle: `${insumo.sku} · ${detalle}`,
    tambien: insumo.category ?? '',
  };
}
